/**
 * Collection pages — mobile spotlight carousel + desktop editorial runway + grid add to cart.
 */
class MobileCollectionSpotlight {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-spotlight-track]');
    this.items = root.querySelectorAll('[data-spotlight-item]');
    if (!this.track || this.items.length === 0) return;

    this.bindScroll();
    this.centerInitial();
  }

  centerInitial() {
    const active = this.track.querySelector('[data-spotlight-item].is-center');
    if (active) {
      active.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'auto' });
    }
  }

  bindScroll() {
    let ticking = false;

    const updateCenter = () => {
      ticking = false;
      const trackRect = this.track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;
      let closest = null;
      let closestDist = Infinity;

      this.items.forEach((item) => {
        const rect = item.getBoundingClientRect();
        const itemCenter = rect.left + rect.width / 2;
        const dist = Math.abs(trackCenter - itemCenter);
        item.classList.toggle('is-center', false);
        if (dist < closestDist) {
          closestDist = dist;
          closest = item;
        }
      });

      closest?.classList.add('is-center');
    };

    this.track.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(updateCenter);
        }
      },
      { passive: true }
    );

    updateCenter();
  }
}

class DesktopCollectionShowcase {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.panels = [...root.querySelectorAll('[data-showcase-panel]')];
    this.thumbs = [...root.querySelectorAll('[data-showcase-thumb]')];
    this.prevBtn = root.querySelector('[data-showcase-prev]');
    this.nextBtn = root.querySelector('[data-showcase-next]');
    this.counter = root.querySelector('[data-showcase-counter]');
    this.filmstrip = root.querySelector('[data-showcase-filmstrip]');
    this.progressSegs = [...root.querySelectorAll('[data-showcase-progress-seg]')];

    if (this.panels.length === 0) return;

    this.activeIndex = this.panels.findIndex((panel) => panel.classList.contains('is-active'));
    if (this.activeIndex < 0) this.activeIndex = 0;

    this.bindControls();
    this.showIndex(this.activeIndex, false);

    this.root.addEventListener('rh:showcase-goto', (event) => {
      const index = Number(event.detail?.index);
      if (!Number.isNaN(index)) this.showIndex(index);
    });
  }

  bindControls() {
    this.prevBtn?.addEventListener('click', () => this.showIndex(this.activeIndex - 1));
    this.nextBtn?.addEventListener('click', () => this.showIndex(this.activeIndex + 1));

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = Number(thumb.dataset.showcaseIndex);
        if (!Number.isNaN(index)) this.showIndex(index);
      });
    });

    this.root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.showIndex(this.activeIndex - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.showIndex(this.activeIndex + 1);
      }
    });
  }

  /** @param {number} index @param {boolean} [focusThumb] */
  showIndex(index, focusThumb = true) {
    const nextIndex = Math.max(0, Math.min(this.panels.length - 1, index));
    const direction = nextIndex > this.activeIndex ? 'next' : nextIndex < this.activeIndex ? 'prev' : '';

    if (nextIndex === this.activeIndex && this.panels[nextIndex]?.classList.contains('is-active')) {
      this.updateArrows();
      return;
    }

    this.activeIndex = nextIndex;

    this.panels.forEach((panel, panelIndex) => {
      const isActive = panelIndex === nextIndex;
      panel.classList.toggle('is-active', isActive);
      panel.dataset.showcaseDirection = isActive && direction ? direction : '';
    });

    this.thumbs.forEach((thumb, thumbIndex) => {
      thumb.classList.toggle('is-active', thumbIndex === nextIndex);
    });

    this.progressSegs.forEach((seg, segIndex) => {
      seg.classList.toggle('is-active', segIndex === nextIndex);
    });

    if (this.counter) {
      const label = String(nextIndex + 1).padStart(2, '0');
      this.counter.innerHTML = `<strong>${label}</strong><span>/ ${this.panels.length}</span>`;
    }

    if (focusThumb) {
      const activeThumb = this.thumbs[nextIndex];
      activeThumb?.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
    }

    this.updateArrows();
  }

  updateArrows() {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.activeIndex <= 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.activeIndex >= this.panels.length - 1;
    }
  }
}

class CollectionPage {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.bindAddToCart();
    this.bindTypeFilters();
  }

  bindTypeFilters() {
    const filterRoot = this.root.querySelector('[data-rh-collection-type-filters]');
    if (!filterRoot) return;

    const chips = [...filterRoot.querySelectorAll('[data-rh-type-filter]')];
    const items = [...this.root.querySelectorAll('[data-product-type]')];
    const countEl = this.root.querySelector('[data-collection-product-count]');
    if (!chips.length || !items.length) return;

    const applyFilter = (type) => {
      let visible = 0;

      items.forEach((item) => {
        const itemType = item.dataset.productType || '';
        const show = type === 'all' || itemType === type;
        item.classList.toggle('is-type-hidden', !show);
        if (show) visible += 1;
      });

      chips.forEach((chip) => {
        const chipType = chip.dataset.rhTypeFilter || 'all';
        chip.classList.toggle('is-active', chipType === type);
      });

      if (countEl) {
        const label = visible === 1 ? 'product' : 'products';
        countEl.textContent = `${visible} ${label} found for you`;
      }

      this.root.querySelectorAll('[data-showcase-progress-seg]').forEach((seg) => {
        const idx = seg.dataset.showcaseIndex;
        const panel = this.root.querySelector(`[data-showcase-panel][data-showcase-index="${idx}"]`);
        seg.classList.toggle('is-type-hidden', panel?.classList.contains('is-type-hidden'));
      });

      const firstVisiblePanel = items.find(
        (item) => item.matches('[data-showcase-panel]') && !item.classList.contains('is-type-hidden')
      );
      if (firstVisiblePanel) {
        const showcaseRoot = this.root.querySelector('[data-desktop-collection-showcase]');
        showcaseRoot?.dispatchEvent(
          new CustomEvent('rh:showcase-goto', {
            detail: { index: Number(firstVisiblePanel.dataset.showcaseIndex) },
          })
        );
      }
    };

    const params = new URLSearchParams(window.location.search);
    const urlType = params.get('filter.p.product_type');
    if (urlType) {
      applyFilter(urlType);
    }

    filterRoot.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-rh-type-filter]');
      if (!chip) return;

      event.preventDefault();
      const type = chip.dataset.rhTypeFilter || 'all';
      applyFilter(type);

      const url = new URL(window.location.href);
      if (type === 'all') {
        url.searchParams.delete('filter.p.product_type');
      } else {
        url.searchParams.set('filter.p.product_type', type);
      }
      window.history.replaceState({}, '', url);
    });
  }

  bindAddToCart() {
    this.root.addEventListener('click', async (event) => {
      const button = event.target.closest('.scp-card__atc--collectionpage');
      if (!button || button.disabled) return;

      event.preventDefault();
      button.disabled = true;
      const originalText = button.textContent;
      button.textContent = 'Adding…';

      try {
        const cart = window.Theme?.storefrontCart;
        if (cart?.addVariantToCart) {
          await cart.addVariantToCart(button.dataset.variantId, 1);
          cart.openThemeCartDrawer?.();
        } else {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }] }),
          });
          if (!response.ok) throw new Error('Add failed');
          document.querySelector('theme-drawer#cart-drawer')?.open?.();
        }
        button.textContent = 'Added';
      } catch (error) {
        console.error(error);
        button.textContent = originalText;
      } finally {
        window.setTimeout(() => {
          button.disabled = false;
          if (button.textContent === 'Added') button.textContent = originalText;
        }, 1200);
      }
    });
  }
}

document.querySelectorAll('[data-mobile-collection-spotlight]').forEach((root) => {
  new MobileCollectionSpotlight(root);
});

document.querySelectorAll('[data-desktop-collection-showcase]').forEach((root) => {
  new DesktopCollectionShowcase(root);
});

document.querySelectorAll('[data-collection-page]').forEach((root) => {
  new CollectionPage(root);
});
